import api from './api';

export type WorkspaceDraftSource = 'INTERNAL' | 'ETSY' | 'INTEGRATION';
export type WorkspaceDraftStatus = 'ACTIVE' | 'PARKED' | 'COMPLETED' | 'ARCHIVED';

export type WorkspaceDraft = {
  readonly id: string;
  readonly userId: string;
  readonly source: WorkspaceDraftSource;
  readonly status: WorkspaceDraftStatus;
  readonly title: string | null;
  readonly serviceId: string | null;
  readonly etsyImportedOrderId: string | null;
  readonly snapshot: Record<string, unknown>;
  readonly metadata: Record<string, unknown> | null;
  readonly lastOpenedAt: string;
  readonly createdAt: string;
  readonly updatedAt: string;
};

type WorkspaceDraftResponse = {
  readonly data: {
    readonly draft: WorkspaceDraft | null;
  };
};

type WorkspaceDraftListResponse = {
  readonly data: {
    readonly drafts: ReadonlyArray<WorkspaceDraft>;
  };
};

export type ActivateWorkspaceDraftPayload = {
  readonly draftId?: string;
  readonly source?: WorkspaceDraftSource;
  readonly title?: string | null;
  readonly serviceId?: string | null;
  readonly etsyImportedOrderId?: string | null;
  readonly snapshot?: Record<string, unknown>;
  readonly metadata?: Record<string, unknown> | null;
};

export type SaveWorkspaceDraftPayload = {
  readonly snapshot?: Record<string, unknown>;
  readonly metadata?: Record<string, unknown> | null;
  readonly title?: string | null;
  readonly status?: WorkspaceDraftStatus;
};

export const fetchWorkspaceDrafts = async (): Promise<ReadonlyArray<WorkspaceDraft>> => {
  const response = await api.get<unknown, WorkspaceDraftListResponse>('/workspace-drafts');
  return response.data.drafts;
};

export const fetchActiveWorkspaceDraft = async (): Promise<WorkspaceDraft | null> => {
  const response = await api.get<unknown, WorkspaceDraftResponse>('/workspace-drafts/active');
  return response.data.draft;
};

export const fetchWorkspaceDraftById = async (draftId: string): Promise<WorkspaceDraft | null> => {
  const response = await api.get<unknown, WorkspaceDraftResponse>(`/workspace-drafts/${draftId}`);
  return response.data.draft;
};

export const activateWorkspaceDraft = async (payload: ActivateWorkspaceDraftPayload): Promise<WorkspaceDraft> => {
  const response = await api.post<unknown, WorkspaceDraftResponse>('/workspace-drafts/activate', payload);
  if (!response.data.draft) {
    throw new Error('Workspace draft activation returned no draft');
  }
  return response.data.draft;
};

export const saveWorkspaceDraft = async (
  draftId: string,
  payload: SaveWorkspaceDraftPayload,
): Promise<WorkspaceDraft> => {
  const response = await api.patch<unknown, WorkspaceDraftResponse>(`/workspace-drafts/${draftId}`, payload);
  if (!response.data.draft) {
    throw new Error('Workspace draft save returned no draft');
  }
  return response.data.draft;
};

export const deleteWorkspaceDraft = async (draftId: string): Promise<WorkspaceDraft> => {
  const response = await api.delete<unknown, WorkspaceDraftResponse>(`/workspace-drafts/${draftId}`);
  if (!response.data.draft) {
    throw new Error('Workspace draft delete returned no draft');
  }
  return response.data.draft;
};
